import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  DollarSign, 
  Users, 
  Target,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  UserPlus,
  BarChart3,
  Gift,
  Calendar,
  Zap,
  Globe,
  Mail,
  ExternalLink
} from "lucide-react";

const commissionTiers = [
  {
    tier: "Bronze",
    requirement: "1-10 sales/month",
    commission: "20%",
    color: "from-amber-500 to-orange-500",
    benefits: ["Basic tracking dashboard", "Email support", "Standard marketing materials"]
  },
  {
    tier: "Silver", 
    requirement: "11-25 sales/month",
    commission: "25%",
    color: "from-gray-400 to-slate-500",
    benefits: ["Advanced analytics", "Priority support", "Custom discount codes", "Monthly webinars"]
  },
  {
    tier: "Gold",
    requirement: "26+ sales/month", 
    commission: "30%",
    color: "from-yellow-400 to-amber-500",
    benefits: ["Dedicated account manager", "Exclusive events", "Co-marketing opportunities", "Early product access"]
  }
];

const features = [
  {
    icon: Target,
    title: "Easy Tracking",
    description: "Comprehensive dashboard to monitor clicks, conversions, and earnings with real-time analytics."
  },
  {
    icon: DollarSign,
    title: "Competitive Rates",
    description: "Industry-leading commission rates from 20-30% with performance-based bonuses."
  },
  {
    icon: Users,
    title: "Marketing Materials",
    description: "Ready-to-use banners, email templates, product images, and promotional content."
  },
  {
    icon: Calendar,
    title: "Monthly Payouts",
    description: "Reliable monthly payments with multiple payout options including PayPal, bank transfer."
  },
  {
    icon: BarChart3,
    title: "Advanced Analytics",
    description: "Detailed reporting on your audience behavior, conversion rates, and optimization tips."
  },
  {
    icon: Zap,
    title: "Quick Approval",
    description: "Fast application review process with approval typically within 2-3 business days."
  }
];

const faqData = [
  {
    question: "How much can I earn as an affiliate?",
    answer: "Affiliates earn 20-30% commission on all successful referrals. Top performers earn $1,000+ per month with our tiered commission structure that rewards high-performing affiliates with higher rates and bonuses."
  },
  {
    question: "How do I get paid?",
    answer: "We pay commissions monthly via PayPal or bank transfer. Minimum payout is $50. Payments are processed within the first week of each month for the previous month's earnings."
  },
  {
    question: "What marketing materials do you provide?",
    answer: "We provide banners, text links, email templates, product images, social media graphics, and detailed product information. All materials are professionally designed and optimized for conversions."
  },
  {
    question: "How long do cookies last?",
    answer: "Our tracking cookies last for 60 days, giving you credit for any purchases made within that time frame after someone clicks your affiliate link."
  },
  {
    question: "Can I promote on social media?",
    answer: "Yes! You can promote SnuggleCrafters on social media, blogs, websites, email newsletters, and other marketing channels. We just ask that you follow our brand guidelines and disclose affiliate relationships as required by law."
  },
  {
    question: "Is there a minimum traffic requirement?",
    answer: "While we don't have strict minimum requirements, we look for affiliates with engaged audiences interested in family content, parenting, children's products, or educational materials."
  }
];

export default function AffiliateInfoPage() {
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <DollarSign className="w-4 h-4" />
              Affiliate Program Information
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              Earn Money Promoting SnuggleCrafters
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Join thousands of affiliates earning competitive commissions by promoting the world's leading 
              AI-powered bedtime story platform. Perfect for family bloggers, parenting influencers, and anyone 
              passionate about children's education and entertainment.
            </p>
            <div className="flex gap-4 justify-center">
              <Link href="/affiliate/signup">
                <Button className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700" data-testid="button-join-now">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Join Affiliate Program
                </Button>
              </Link>
              <Link href="/programs">
                <Button variant="outline" data-testid="button-back-programs">
                  View All Programs
                </Button>
              </Link>
            </div>
          </div>

          {/* Commission Tiers */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Commission Tiers</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {commissionTiers.map((tier, index) => (
                <Card key={index} className={`relative overflow-hidden border-2 ${index === 2 ? 'border-yellow-300 dark:border-yellow-600' : 'border-gray-200 dark:border-gray-700'}`}>
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${tier.color} rounded-bl-[60px] opacity-10`}></div>
                  <CardHeader className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-xl">{tier.tier}</CardTitle>
                      {index === 2 && (
                        <Badge className="bg-yellow-500 text-yellow-900">Most Popular</Badge>
                      )}
                    </div>
                    <div className="text-3xl font-bold mb-2">{tier.commission}</div>
                    <CardDescription>{tier.requirement}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {tier.benefits.map((benefit, bidx) => (
                        <li key={bidx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Why Choose Our Affiliate Program?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: "1",
                  title: "Apply",
                  description: "Fill out our simple application form with your website and marketing details"
                },
                {
                  step: "2", 
                  title: "Get Approved",
                  description: "We review applications within 2-3 business days and provide access to our affiliate portal"
                },
                {
                  step: "3",
                  title: "Promote",
                  description: "Use our marketing materials to promote SnuggleCrafters to your audience"
                },
                {
                  step: "4",
                  title: "Earn",
                  description: "Receive monthly commission payments for every successful referral"
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {step.step}
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="max-w-4xl mx-auto">
              {faqData.map((faq, index) => (
                <Card key={index} className="mb-4">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="text-center bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950 dark:to-amber-950 border-2 border-orange-200 dark:border-orange-800">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Earning?</h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Join our affiliate program today and start earning commissions promoting the world's most 
                innovative bedtime story platform for children.
              </p>
              <div className="flex gap-4 justify-center">
                <Link href="/affiliate/signup">
                  <Button className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700" data-testid="button-join-affiliate">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Join Affiliate Program
                  </Button>
                </Link>
                <Link href="/contact">
                  <Button variant="outline" data-testid="button-contact-us">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Us
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}